<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg', body: { base: 'overflow-visible' } }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">
                        Subscribe Customer
                    </h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div class="space-y-4">
                <UFormGroup name="partner_id" label="Partner" required>
                    <UInputMenu
                        v-model="selectedPartner"
                        @update:model-value="onPartnerSelected"
                        :loading="partnerSearching"
                        :search="searchPartner"
                        placeholder="Choose a partner..."
                        option-attribute="name"
                        size="lg"
                        trailing
                        by="id"
                    />
                </UFormGroup>

                <template v-if="selectedPartner && subscriptionFields.length">
                    <div class="space-y-4">
                        <UFormGroup
                            v-for="field in subscriptionFields"
                            :key="field.key"
                            :name="field.key"
                            :label="field.label"
                            :required="field.required"
                        >
                            <UInput v-model="formFields[field.key]" size="lg" />
                        </UFormGroup>
                    </div>
                </template>
            </div>

            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white" :disabled="subscribing">
                        Cancel
                    </UButton>
                    <UButton
                        @click="onSubmit()"
                        size="lg"
                        color="black"
                        :loading="subscribing"
                        :disabled="!canSubmit"
                    >
                        Subscribe
                    </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { Partner } from '~/types/partner'

const emit = defineEmits<{
    'update:open': [boolean],
    'subscribed': [void]
}>()

const props = defineProps<{
    open: boolean
    customerId?: string
}>()

const client = useSupabaseClient()
const toast = useToast()

const subscribing = ref<boolean>(false)
const partnerSearching = ref<boolean>(false)
const selectedPartner = ref<Partial<Partner> | undefined>()
const formFields = ref<Record<string, string>>({})

const subscriptionFields = computed(() => {
    const fields = selectedPartner.value?.metadata?.subscription_fields || []
    return fields.filter(f => f.required)
})

const canSubmit = computed(() => {
    if (!selectedPartner.value || !props.customerId) return false
    return subscriptionFields.value.every(field => formFields.value[field.key]?.trim())
})

watch(() => props.open, (open) => {
    if (open) {
        resetForm()
    }
})

function resetForm() {
    selectedPartner.value = undefined
    formFields.value = {}
    subscribing.value = false
}

async function searchPartner(q: string) {
    partnerSearching.value = true

    const { data, error } = await client
        .from('partners')
        .select('id, name, metadata')
        .is('deleted_at', null)
        .eq('metadata ->> is_public', true)
        .ilike('name', `%${q}%`)
        .order('name')

    partnerSearching.value = false

    if (error) {
        console.error('Error searching partners:', error)
        return []
    }

    return data || []
}

function onPartnerSelected(partner: Partial<Partner>) {
    selectedPartner.value = partner
    formFields.value = {}
}

async function onSubmit() {
    if (!props.customerId || !selectedPartner.value?.id) return

    subscribing.value = true

    try {
        await $fetch('/api/subscriptions', {
            method: 'POST',
            body: {
                customer_id: props.customerId,
                type: 'credential',
                partner_id: selectedPartner.value.id,
                fields: formFields.value
            }
        })

        toast.add({
            title: 'Customer subscribed successfully!',
            color: 'green',
            icon: 'i-ph-check-circle-duotone'
        })

        emit('subscribed')
        dismiss()
    } catch (e: any) {
        const errorMessage = e?.data?.message || e?.message || 'Please try again.'

        toast.add({
            title: 'Could not subscribe customer.',
            description: errorMessage,
            color: 'red',
            icon: 'i-ph-warning-duotone'
        })

        console.error('Could not subscribe customer:', e)
    } finally {
        subscribing.value = false
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>